import { getFontEmbedCSS, toJpeg, toPng } from 'html-to-image'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '~/design-system/components/DeckGen/types/presentation'
import { loadFontByFamily } from '~/design-system/fonts/loadFont'

/**
 * Snapshot a rendered DeckGen slide to a PNG data URL for the vision review pass (Layer 2 of
 * verify-after-edit). The slide surface is tagged `data-deckgen-slide-surface` and renders at the
 * full 1920x1080 model size (an ancestor CSS transform scales it on screen). We capture the node
 * at its native size and downscale via pixelRatio so the payload stays small.
 *
 * Returns null on any failure — callers MUST degrade gracefully to the deterministic lint, which
 * is always available. The screenshot is an enhancement, never a hard dependency.
 */

// Font-embed caches (per session): the same-origin embed (computed once), the parsed REMOTE @font-face
// rules grouped by family (Google Fonts + Fontshare + any cross-origin provider — re-scanned for newly
// injected links on every capture, because fonts load LAZILY as slides come to use them), and each
// embedded (data-URL) @font-face by family (fetched on first use). Scoping to the families a slide
// actually uses keeps the payload tiny even though the app loads hundreds of font-picker options.
let sameOriginEmbedCache: string | null = null
const remoteFacesByFamily = new Map<string, string[]>()
const parsedStylesheetHrefs = new Set<string>()
const embeddedFacesByFamily = new Map<string, string>()

export interface CaptureSlideOptions {
  /** Output scale. 0.5 → ~960x540 (the small vision-pass default); 2 → crisp 3840x2160 for a PDF page. */
  pixelRatio?: number
  /** 'png' (default, lossless — the vision pass) or 'jpeg' (much smaller — good for a multi-slide PDF). */
  format?: 'png' | 'jpeg'
  /** JPEG quality 0–1 (ignored for png). */
  quality?: number
  /** Fill behind the slide for JPEG (no alpha). Pass the slide's own bg color so a dropped
   *  background-image/gradient layer renders as the right color instead of white. Default white. */
  backgroundColor?: string
}

export async function captureSlide(slideId: string, opts: CaptureSlideOptions = {}): Promise<string | null> {
  if (typeof document === 'undefined') return null
  const node = findSlideSurface(slideId)
  if (!node) return null
  // PROACTIVELY load the fonts THIS slide uses BEFORE waiting on fonts.ready. On deck OPEN a slide's
  // font <link> may not be injected yet (fonts load lazily as slides come into view), so document.fonts
  // .ready would resolve INSTANTLY with the font still absent — and the snapshot bakes a FALLBACK glyph.
  // Because the content hash doesn't change afterward, that wrong capture persists, and the slide then
  // visibly SHIFTS font every time it swaps bitmap↔live-DOM (the "old decks change font on zoom"). Loading
  // the used families first registers their @font-face + fetches the files so the embed + snapshot are
  // correct. (This is the DOM-node analogue of ensureFontsForHtml.)
  await preloadNodeFonts(node)
  // Never snapshot before webfonts finish: a capture taken with fallback fonts is baked wrong. Resolves
  // instantly once the fonts loaded above are ready, so steady-state cost is zero.
  try {
    await document.fonts.ready
  } catch {
    /* non-blocking */
  }

  // Default to the small lossless capture the vision pass relies on; callers (e.g. PDF export) can
  // ask for a crisp JPEG instead.
  const { pixelRatio = 0.5, format = 'png', quality = 0.92, backgroundColor = '#ffffff' } = opts
  // No cacheBust: it forced a fresh network download of every image on the slide (including the photo
  // an edit just placed and preloaded), adding seconds — slide image urls are stable within a session,
  // so the HTTP cache is exactly what we want. Neutralize the on-screen scale transform on the clone so
  // it renders at native 1920x1080. Also force `content-visibility:visible`: the canvas preview surface
  // sets `content-visibility:auto` for perf, so an OFF-SCREEN slide (e.g. every page of a PDF export) is
  // unrendered — without this the clone would serialize blank. Forcing it visible always renders the full tree.
  const style = { transform: 'none', transformOrigin: 'top left', margin: '0', contentVisibility: 'visible' }

  try {
    // Per-node: same-origin fonts + only the Google-hosted families THIS slide actually uses (font data
    // is fetched once per family and cached, so this is cheap after the first slide to use each font).
    const fontEmbedCSS = await computeFontEmbedCSS(node)
    if (format === 'jpeg') {
      return await toJpeg(node, {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        pixelRatio,
        quality,
        // JPEG has no alpha — fill behind the slide (caller passes the slide's bg color; default white)
        // so a transparent/dropped background layer isn't rendered black or white.
        backgroundColor,
        fontEmbedCSS,
        style,
      })
    }
    return await toPng(node, {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      pixelRatio,
      fontEmbedCSS,
      style,
    })
  } catch {
    return null
  }
}

/** Wait until every given slide id has a live capture surface mounted (or the timeout passes). The PDF
 *  export forces LOD'd cards back into live mode right before capturing; React needs a frame or two to
 *  mount those surfaces, and capturing before they exist silently skips slides. */
export async function waitForSlideSurfaces(slideIds: readonly string[], timeoutMs = 3000): Promise<void> {
  if (typeof document === 'undefined') return
  const deadline = performance.now() + timeoutMs
  while (performance.now() < deadline) {
    if (slideIds.every((id) => findSlideSurface(id))) return
    await new Promise((r) => requestAnimationFrame(r))
  }
}

/** The largest matching surface — the main canvas, not a tiny thumbnail of the same slide. */
function findSlideSurface(slideId: string): HTMLElement | null {
  const matches = Array.from(
    document.querySelectorAll<HTMLElement>(`[data-deckgen-slide-surface][data-slide-id="${cssEscape(slideId)}"]`),
  )
  if (matches.length === 0) return null
  return matches.reduce((largest, el) =>
    el.getBoundingClientRect().width > largest.getBoundingClientRect().width ? el : largest,
  )
}

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value)
  return value.replace(/["\\]/g, '\\$&')
}

/**
 * The @font-face CSS to inline into a capture. html-to-image's getFontEmbedCSS handles same-origin
 * stylesheets, but CANNOT read cross-origin ones — it can't access their cssRules — so a slide using a
 * remotely-hosted web font captures with a FALLBACK font, which reads as a font swap vs the live slide
 * when you zoom. We fix that here for EVERY cross-origin provider (Google Fonts, Fontshare — General
 * Sans, Deck's own brand font, is Fontshare-hosted — and anything future): fetch the stylesheet TEXT
 * (fetch is allowed cross-origin) and inline the font FILES as data: URLs. Fonts inject lazily as slides
 * come to use them, so the link scan re-runs per capture and picks up anything new; parsed hrefs and
 * embedded families are cached, so the steady-state cost is one querySelectorAll. Everything is
 * best-effort — any failure just falls back to the base embed.
 */
/** Load every font family the slide's DOM actually uses (register its @font-face link + fetch the file),
 *  so a subsequent snapshot renders with the real glyphs instead of a fallback. Bounded so a slow/blocked
 *  font provider can't stall a capture indefinitely. */
async function preloadNodeFonts(node: HTMLElement): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) return
  const used = new Set<string>()
  for (const el of Array.from(node.querySelectorAll<HTMLElement>('*'))) {
    const fam = getComputedStyle(el).fontFamily.split(',')[0]?.replace(/["']/g, '').trim()
    if (fam) used.add(fam)
  }
  if (used.size === 0) return
  for (const fam of used) loadFontByFamily(fam)
  // Let the injected <link>s register their @font-face rules before forcing the load.
  await new Promise((r) => setTimeout(r, 50))
  const loads: Promise<unknown>[] = []
  for (const fam of used) {
    for (const weight of ['400', '700']) loads.push(document.fonts.load(`${weight} 16px "${fam}"`).catch(() => {}))
  }
  await Promise.race([Promise.allSettled(loads), new Promise((r) => setTimeout(r, 2000))])
}

/** The embedded (data-URL) @font-face CSS for a set of font families — the SAME cross-origin font
 *  embedding `captureSlide` performs, exposed for other rasterizers (e.g. the lab whiteboard's SVG
 *  thumbnails) so their bitmaps render with the REAL web font instead of a system fallback. Without this a
 *  bitmap and the live DOM twin disagree on letter metrics, and the slide visibly reflows when it swaps
 *  bitmap↔DOM on zoom. Families match case-insensitively; system/same-origin families yield nothing.
 *  Cached per family across the session (shares the module caches with captureSlide). Best-effort: any
 *  fetch failure just yields an empty string and the caller degrades to the fallback glyph. */
/** What {@link embedRemoteFontFacesDetailed} answers: the CSS, and what it could not account for. */
export interface EmbeddedFontFaces {
  /** `@font-face` blocks with the files inlined as data URIs, ready to drop into an SVG's `<style>`. */
  css: string
  /**
   * Families this could not resolve BECAUSE ITS KNOWLEDGE IS INCOMPLETE — a stylesheet it needed did not
   * arrive. Not the same as a family with nothing to embed: a system or local face resolves to no CSS and
   * is perfectly correct, and is not listed here.
   *
   * A caller rendering to a sandboxed surface (an SVG image cannot fetch anything) must treat a non-empty
   * list as "these pixels are wrong" — the text will render in a fallback — and above all must not
   * PERSIST them. That is what turned a slow reload into thumbnails that were permanently wrong: the
   * cache key is the artifact's HTML, so a bitmap baked with the wrong font came back on every future
   * load without anything ever re-rendering it.
   */
  missing: string[]
}

export async function embedRemoteFontFacesDetailed(families: string[]): Promise<EmbeddedFontFaces> {
  const parts: string[] = []
  const missing: string[] = []
  try {
    const complete = await refreshRemoteFontFaces()
    for (const raw of families) {
      const fam = raw.replace(/["']/g, '').trim().toLowerCase()
      if (!fam) continue
      if (embeddedFacesByFamily.has(fam)) {
        const cached = embeddedFacesByFamily.get(fam)
        if (cached) parts.push(cached)
        continue
      }
      const faces = remoteFacesByFamily.get(fam)
      if (!faces) {
        // ONLY CACHE A NEGATIVE WE CAN STAND BEHIND. With every stylesheet parsed, "no faces" means the
        // family is system or same-origin and there is nothing to embed — the answer is final and worth
        // remembering. With one still missing it means "we do not know yet", and remembering THAT is the
        // bug: the family would bake as a fallback for the rest of the session, however quickly its
        // stylesheet turned up a moment later.
        if (complete) embeddedFacesByFamily.set(fam, '')
        else missing.push(fam)
        continue
      }
      const embedded = (await Promise.all(faces.map(embedFontFace))).filter(Boolean).join('\n')
      embeddedFacesByFamily.set(fam, embedded)
      if (embedded) parts.push(embedded)
      // Faces found but every one of them unfetchable: the family is remote and we have nothing for it,
      // which is the same "wrong pixels" answer as not knowing.
      else missing.push(fam)
    }
  } catch {
    /* remote fonts are a best-effort enhancement */
  }
  return { css: parts.filter(Boolean).join('\n'), missing }
}

/** The CSS alone, for callers with nothing to decide on a miss. */
export async function embedRemoteFontFaces(families: string[]): Promise<string> {
  return (await embedRemoteFontFacesDetailed(families)).css
}

async function computeFontEmbedCSS(node: HTMLElement): Promise<string> {
  const parts: string[] = []
  if (sameOriginEmbedCache == null) {
    try {
      sameOriginEmbedCache = await getFontEmbedCSS(node)
    } catch {
      sameOriginEmbedCache = ''
    }
  }
  parts.push(sameOriginEmbedCache)

  // Which remotely-hosted families does THIS slide use? (first family token of every element's computed font).
  const used = new Set<string>()
  for (const el of Array.from(node.querySelectorAll<HTMLElement>('*'))) {
    const fam = getComputedStyle(el).fontFamily.split(',')[0]?.replace(/["']/g, '').trim().toLowerCase()
    if (fam) used.add(fam)
  }
  try {
    await refreshRemoteFontFaces()
    for (const fam of used) {
      if (embeddedFacesByFamily.has(fam)) {
        if (embeddedFacesByFamily.get(fam)) parts.push(embeddedFacesByFamily.get(fam)!)
        continue
      }
      const faces = remoteFacesByFamily.get(fam)
      if (!faces) {
        embeddedFacesByFamily.set(fam, '') // not remote-hosted (same-origin/system) — nothing to add
        continue
      }
      const embedded = (await Promise.all(faces.map(embedFontFace))).filter(Boolean).join('\n')
      embeddedFacesByFamily.set(fam, embedded)
      if (embedded) parts.push(embedded)
      // Diagnostic: the family HAS remote faces but none could be embedded (font file fetch failed) —
      // this slide's snapshot will bake a fallback font for it.
      else console.warn('[deckgen-capture] font family could not be embedded (snapshot will use fallback):', fam)
    }
  } catch {
    /* remote fonts are a best-effort enhancement */
  }
  return parts.filter(Boolean).join('\n')
}

/** Fetch any NOT-YET-PARSED cross-origin stylesheet's text and fold its @font-face rules into the
 *  by-family map. Covers every font provider (Google, Fontshare, …) and — because it re-runs per
 *  capture — fonts whose <link> was injected lazily after earlier captures. */
/** Fetch a cross-origin resource the browser would CORS-block, falling back to the same-origin
 *  image/font proxy (server-side fetch, no CORS). Provider font CSS + font files are the two things the
 *  capture path needs but the browser can't fetch directly from Fontshare/Google. Returns null on total
 *  failure so the caller degrades to a fallback glyph rather than throwing. */
async function fetchFontResource(url: string): Promise<Response | null> {
  const absolute = url.startsWith('//') ? `https:${url}` : url
  // A fonts URL carrying an UNRESOLVED css token (family=var(--a-mono)) can never succeed: the provider
  // 400s it, the proxy 502s it, and each attempt burns the 4s timeout TWICE inside whatever capture is
  // waiting. These come from malformed <link> tags inside slide html where a token was interpolated
  // instead of a family name; skipping them here degrades those captures to fallback glyphs instantly.
  if (absolute.includes('var(')) return null
  for (const u of [absolute, `/deckgen-image-proxy-api?url=${encodeURIComponent(absolute)}`]) {
    try {
      // Bounded: fonts are an enhancement, and one stalled CDN request here used to hang the WHOLE
      // multi-slide export forever (no timeout anywhere up the chain). Time out → fallback glyphs.
      const r = await fetch(u, { signal: AbortSignal.timeout(4000) })
      if (r.ok) return r
    } catch {
      /* try the proxy next */
    }
  }
  return null
}

/**
 * How many times one stylesheet may fail before we stop asking.
 *
 * A failed fetch used to be indistinguishable from a parsed one: the href was marked parsed BEFORE the
 * request went out, so one bad response poisoned that family for the life of the page. Retrying instead
 * — but not forever, because a permanently dead URL would then cost a fetch on every raster.
 */
const STYLESHEET_TRIES = 3
const stylesheetTries = new Map<string, number>()

/**
 * Pull every remote stylesheet's `@font-face` rules into `remoteFacesByFamily`.
 *
 * RETURNS WHETHER THE ANSWER IS COMPLETE, and that boolean is the whole point of this signature. A
 * family missing from the map means one of two very different things — it is a system or local face and
 * there is genuinely nothing to embed, or a stylesheet we needed did not arrive. Callers cached the
 * first reading either way, so a single slow or failed fetch on a reload turned into "this family bakes
 * as a fallback, permanently", and every thumbnail rastered afterwards was written to the persistent
 * cache with the wrong font in it (Beka, 2026-09-10: "some of the artifact thumbnails show artifacts
 * with wrong font" after a reload).
 */
async function refreshRemoteFontFaces(): Promise<boolean> {
  const hrefs = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
    .map((l) => l.href)
    .filter((h) => {
      try {
        if (new URL(h).origin === window.location.origin) return false
        if (parsedStylesheetHrefs.has(h)) return false
        return (stylesheetTries.get(h) ?? 0) < STYLESHEET_TRIES
      } catch {
        return false
      }
    })
  if (hrefs.length === 0) return true
  const results = await Promise.all(
    hrefs.map(async (href) => {
      // Direct fetch first, then the same-origin proxy — most provider CSS endpoints (Fontshare, and
      // in some environments Google) don't send CORS, so a direct fetch fails and the proxy is what
      // actually gets the @font-face rules (fixing the "fonts change on zoom" fallback baking).
      const res = await fetchFontResource(href)
      if (res) return { href, css: await res.text(), ok: true }
      console.warn('[deckgen-capture] cannot fetch font stylesheet, even via proxy (families bake as fallback):', href)
      return { href, css: '', ok: false }
    }),
  )
  let complete = true
  for (const r of results) {
    // MARKED ON SUCCESS ONLY. This used to run before the fetch, which is what made a transient failure
    // permanent — the href was never asked for again.
    if (r.ok) {
      parsedStylesheetHrefs.add(r.href)
      stylesheetTries.delete(r.href)
      continue
    }
    const tries = (stylesheetTries.get(r.href) ?? 0) + 1
    stylesheetTries.set(r.href, tries)
    // Out of tries is not the same as answered: it is us giving up, and a family that depended on this
    // sheet still has no faces. Saying so keeps the negative cache below closed for good rather than
    // letting a dead URL quietly re-enable it.
    complete = false
  }
  const faces = results.map((r) => r.css).join('\n').match(/@font-face\s*\{[^}]*\}/g) ?? []
  for (const face of faces) {
    const fam = face.match(/font-family:\s*['"]?([^;'"]+)['"]?\s*;/i)?.[1]?.trim().toLowerCase()
    if (!fam) continue
    if (!remoteFacesByFamily.has(fam)) remoteFacesByFamily.set(fam, [])
    remoteFacesByFamily.get(fam)!.push(face)
    // The family gained faces — a previously-cached (possibly empty) embed for it is now stale.
    embeddedFacesByFamily.delete(fam)
  }
  return complete
}

async function embedFontFace(face: string): Promise<string> {
  // Match every provider's URL style: Google writes bare absolute urls `url(https://…)`; Fontshare
  // writes QUOTED PROTOCOL-RELATIVE ones `url('//cdn.fontshare.com/….woff2')`. The old https-only,
  // unquoted pattern silently skipped every Fontshare face — General Sans (Deck's brand font) baked as
  // a fallback in every snapshot, which was the stubborn "fonts change on zoom".
  const urlMatch = face.match(/url\(\s*['"]?((?:https?:)?\/\/[^)'"\s]+\.(?:woff2?|ttf|otf))['"]?\s*\)/)
  if (!urlMatch) return '' // no fetchable src — drop rather than reference an unembeddable url
  const fontUrl = urlMatch[1].startsWith('//') ? `https:${urlMatch[1]}` : urlMatch[1]
  try {
    // Direct fetch first, then the proxy (gstatic/Fontshare font files usually send CORS, but proxy
    // covers the ones that don't — same reason as the CSS fetch above).
    const res = await fetchFontResource(fontUrl)
    if (!res) return ''
    const buf = await res.arrayBuffer()
    const format = fontUrl.endsWith('.woff2') ? 'woff2' : fontUrl.endsWith('.woff') ? 'woff' : 'truetype'
    // Replace the ENTIRE src list (all url(...) alternatives) with the one embedded file, so no
    // unfetchable external url survives in the serialized capture.
    return face.replace(/src:[^;]+;/i, `src: url(data:font/${format};base64,${bufferToBase64(buf)}) format('${format}');`)
  } catch {
    return ''
  }
}

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}
